import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import FlightOutlinedIcon from "@mui/icons-material/FlightOutlined";
import { ACCENT } from "./accent";
import { useAuthGate } from "../../../hooks/useAuthGate";
import LogDropzone from "./LogDropzone";
import { listPublicLogs, uploadPublicLogOrDemo } from "./api/publicTelemetry";
import { logPath } from "./paths";
import { flightRank, letterGrade, scoreTone } from "./ScoreHeader";
import CollectionToolbar, { FilterChips } from "../../Templates/CollectionToolbar";
import CollectionTable from "../../Templates/CollectionTable";
import { Link as RouterLink } from "react-router-dom";

const POLL_MS = 30000; // reshuffle the gallery periodically (backend randomises order)

const fmtDuration = (s) => {
  if (s == null) return "—";
  const t = Math.round(s);
  const m = Math.floor(t / 60);
  return m > 0 ? `${m}m ${t % 60}s` : `${t}s`;
};

const LogCard = ({ log, to }) => {
  const theme = useTheme();
  const s = log.summary || {};
  // The list payload carries the summary, so the rules pass — and the rank it
  // scores — runs here without opening the log.
  const rank = flightRank(log);
  return (
    <Paper
      elevation={0}
      component={RouterLink}
      to={to}
      sx={{
        p: 2,
        display: "block",
        textDecoration: "none",
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        borderRadius: 2,
        transition: "border-color .15s, transform .15s",
        "&:hover": { borderColor: ACCENT, transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <FlightOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
        <Typography noWrap sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {log.file_name}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
        {rank != null && (
          <Chip
            label={`${rank} · ${letterGrade(rank)}`}
            size="small"
            sx={{
              fontWeight: 700,
              color: scoreTone(theme, rank),
              backgroundColor: alpha(scoreTone(theme, rank), 0.12),
              border: `1px solid ${alpha(scoreTone(theme, rank), 0.35)}`,
            }}
          />
        )}
        {log.vehicle_type && (
          <Chip label={log.vehicle_type} size="small" sx={{ backgroundColor: `${ACCENT}14`, color: ACCENT }} />
        )}
        <Chip label={fmtDuration(log.duration_s)} size="small" variant="outlined" />
        {(log.tags || []).slice(0, 2).map((t) => (
          <Chip key={t} label={t} size="small" variant="outlined" />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
        {/* Joined rather than concatenated with leading separators: the board
            is only known for logs parsed since it was recorded, so any of these
            can be absent and a fixed " · " prefix would show the gap. */}
        {[
          log.board_model,
          s.gps?.sats_min != null && `Min sats ${s.gps.sats_min}`,
          s.battery?.volt_min != null && `Min ${s.battery.volt_min}V`,
          s.firmware,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
    </Paper>
  );
};

/** The card's facts, as columns. Same data, denser reading. */
const LOG_COLUMNS = [
  { key: "file_name", label: "Log" },
  {
    key: "rank",
    label: "Rank",
    render: (log) => {
      const rank = flightRank(log);
      return rank == null ? "—" : `${rank} · ${letterGrade(rank)}`;
    },
  },
  { key: "board_model", label: "Board" },
  { key: "vehicle_type", label: "Vehicle" },
  { key: "duration_s", label: "Duration", align: "right", render: (log) => fmtDuration(log.duration_s) },
  { key: "firmware", label: "Firmware", render: (log) => log.summary?.firmware || "—" },
];

const PublicLogAnalyzer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthGate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [vehicle, setVehicle] = useState("all");
  const [view, setView] = useState("cards");
  const firstLoad = useRef(true);

  // Vehicle types actually present, so the filter never offers an empty result.
  const vehicles = useMemo(
    () => [...new Set(logs.map((l) => l.vehicle_type).filter(Boolean))].sort(),
    [logs]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return logs.filter((log) => {
      if (vehicle !== "all" && log.vehicle_type !== vehicle) return false;
      if (!q) return true;
      // The board is searchable too: "which of these flew a CubeOrange" is the
      // question the flight-hours chart makes people want to ask.
      return [log.file_name, log.vehicle_type, log.board_model, ...(log.tags || [])]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
    });
  }, [logs, query, vehicle]);

  const refresh = useCallback(async () => {
    try {
      const data = await listPublicLogs({ limit: 24 });
      setLogs(data);
    } catch {
      /* gallery is best-effort — leave prior data on error */
    } finally {
      if (firstLoad.current) {
        setLoading(false);
        firstLoad.current = false;
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // No account needed to get a first analysis: the log uploads anonymously and
  // the detail page picks it up mid-parse and polls. Anonymous uploads are
  // public — that's the trade, and it's stated up front.
  const onUploaded = (logId) => navigate(logPath(logId));

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {/* Hero */}
      <Box sx={{ px: 4, pt: { xs: 6, md: 10 }, pb: 6, textAlign: "center", maxWidth: 760, mx: "auto" }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.primary }}>
          Analyze your flight log
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 400, mb: 4, color: theme.palette.text.secondary }}>
          Drop an ArduPilot log and get a summary, charts and a timeline — no account.
          Or explore logs the community has shared below.
        </Typography>

        <Box sx={{ maxWidth: 620, mx: "auto", textAlign: "left" }}>
          <LogDropzone upload={isAuthenticated ? undefined : uploadPublicLogOrDemo} onUploaded={onUploaded} />
        </Box>

        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: theme.palette.text.secondary }}>
          {isAuthenticated
            ? "Uploads land in your account."
            : "Logs uploaded without an account are public. Sign in to keep yours private and to ask follow-up questions."}
        </Typography>
      </Box>

      {/* Community gallery */}
      <Box sx={{ px: 4, pb: 8, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, letterSpacing: 0.4 }}>
          FROM THE COMMUNITY
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress color="secondary" size={28} />
          </Box>
        ) : logs.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            No public logs yet — be the first to share one.
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <CollectionToolbar
                query={query}
                onQueryChange={setQuery}
                placeholder="Search a log, board or tag…"
                view={view}
                onViewChange={setView}
              >
                {vehicles.length > 1 && (
                  <FilterChips
                    value={vehicle}
                    onChange={setVehicle}
                    options={vehicles.map((v) => ({ value: v, label: v }))}
                  />
                )}
              </CollectionToolbar>
            </Box>

            <Typography variant="caption" sx={{ display: "block", mb: 2, color: theme.palette.text.secondary }}>
              {shown.length} of {logs.length} {logs.length === 1 ? "log" : "logs"}
            </Typography>

            {shown.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Nothing matches that. Clear the search or pick a different vehicle.
              </Typography>
            ) : view === "table" ? (
              <CollectionTable
                columns={LOG_COLUMNS}
                rows={shown}
                rowKey={(log) => log.id}
                onRowClick={(log) => navigate(logPath(log.id))}
              />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 2,
                }}
              >
                {shown.map((log) => (
                  <LogCard key={log.id} log={log} to={logPath(log.id)} />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PublicLogAnalyzer;
